import { useRef, useState, useEffect, useCallback } from 'react';
import {
  Bell,
  MessageSquare,
  ChevronRight,
  CheckCheck,
  FileText,
  Video,
  CalendarClock,
  CalendarX,
  CreditCard,
  Info,
  Loader2,
} from 'lucide-react';
import { formatDistance } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../../../../../theme/ThemeProvider';
import { motion, AnimatePresence } from 'framer-motion';
import { useNotifications } from 'src/hooks/useNotifications';
import type { NotificationKind } from 'src/shared/Services/apiService/PlatformTypes';

/**
 * Icon per notification kind. Falls back to a neutral glyph so a kind the
 * backend adds later renders as a normal row rather than crashing on an
 * undefined component.
 */
const KIND_ICONS: Record<NotificationKind, typeof Bell> = {
  report_ready: FileText,
  system: Info,
  video_scheduled: Video,
  video_cancelled: CalendarX,
  video_rescheduled: CalendarClock,
  payment: CreditCard,
  message: MessageSquare,
};

const PAGE_SIZE = 20;
/** Backend caps `limit` at 100. */
const MAX_LIMIT = 100;

/** Guards against an invalid/absent `createdAt` rendering as "Invalid Date". */
const formatWhen = (iso: string): string => {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  return formatDistance(t, new Date(), { addSuffix: true });
};

function HeaderNotifications() {
  const ref = useRef<HTMLDivElement>(null);
  const [isOpen, setOpen] = useState(false);
  const { isDark } = useThemeMode();
  const navigate = useNavigate();
  /**
   * There is no dedicated notifications route in this app, so "show more"
   * expands the panel in place up to the backend's documented ceiling (100)
   * rather than linking somewhere that does not exist.
   */
  const [limit, setLimit] = useState(PAGE_SIZE);
  const {
    notifications,
    unreadCount,
    loading,
    error,
    markRead,
    markAllRead,
  } = useNotifications(limit);
  const canLoadMore = notifications.length >= limit && limit < MAX_LIMIT;

  const handleClose = useCallback(() => setOpen(false), []);

  /**
   * Mark read, then follow the row's link. The read call is not awaited: it is
   * optimistic in the hook, and making the user wait on it before navigating
   * would add a visible stall for no benefit.
   */
  const handleNotificationClick = useCallback(
    (notifId: string, link?: string | null, unread?: boolean) => {
      if (unread) markRead(notifId);
      handleClose();
      if (link) navigate(link);
    },
    [markRead, handleClose, navigate],
  );

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) handleClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, handleClose]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, handleClose]);

  return (
    <div ref={ref} className="relative">
      {/* Trigger button */}
      <motion.button
        whileTap={{ scale: 0.9 }}
        onClick={() => setOpen(!isOpen)}
        className={[
          'relative p-2 rounded-xl min-h-[40px] min-w-[40px]',
          'flex items-center justify-center',
          'transition-all duration-200 group',
          isDark
            ? 'text-slate-400 hover:text-slate-100 hover:bg-white/[0.07]'
            : 'text-slate-400 hover:text-brand-500 hover:bg-brand-50/70',
          isOpen && (isDark ? 'bg-white/[0.07] text-slate-100' : 'bg-brand-50/80 text-brand-500'),
        ].join(' ')}
        aria-label="Notifications"
        title="Notifications"
      >
        <Bell size={17} strokeWidth={1.9} className="transition-transform duration-200 group-hover:scale-105" />
        {/* Badge */}
        {unreadCount > 0 && (
          <span className="absolute top-1.5 right-1.5 flex items-center justify-center">
            <span className="w-[8px] h-[8px] rounded-full bg-red-500 ring-[1.5px] ring-white dark:ring-surface-dark" />
            <span className="absolute w-[8px] h-[8px] rounded-full bg-red-400/60 animate-ping [animation-duration:2s]" />
          </span>
        )}
      </motion.button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={handleClose} />
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.97 }}
              transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
              className={[
                'absolute -right-[60px] sm:right-0 mt-2.5 z-50 w-[320px] sm:w-[380px] max-w-[calc(100vw-32px)] overflow-hidden rounded-2xl',
                isDark
                  ? 'bg-dark-chrome-dropdown shadow-dark-dropdown border border-dark-border'
                  : 'bg-light-surface-panel shadow-light-dropdown border border-light-border',
              ].join(' ')}
              style={{
                backdropFilter: 'blur(40px) saturate(1.6)',
                WebkitBackdropFilter: 'blur(40px) saturate(1.6)',
              }}
            >
              {/* Animated brand bar */}
              <div className="h-[2.5px] bg-gradient-to-r from-brand-700 via-brand-500 via-brand-400 to-brand-300 bg-[length:200%_100%] animate-shimmer" />

              {/* Header */}
              <div className={[
                'flex items-center justify-between px-5 py-3.5',
                'border-b',
                isDark ? 'border-white/[0.05]' : 'border-slate-100',
              ].join(' ')}>
                <div className="flex items-center gap-2.5">
                  <div
                    className={[
                      'w-7 h-7 rounded-lg flex items-center justify-center',
                      isDark ? 'bg-dark-icon-bg-brand' : 'bg-brand-500/[0.08]',
                    ].join(' ')}
                  >
                    <Bell size={13} className={"text-brand-500 dark:text-brand-400"} strokeWidth={2} />
                  </div>
                  <h3 className={[
                    'text-body-xs font-bold',
                    "text-slate-800 dark:text-white",
                  ].join(' ')}>
                    Notifications
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                  {unreadCount > 0 && (
                    <span className={[
                      'inline-flex items-center gap-1 px-2 py-0.5 rounded-full',
                      'text-th font-bold uppercase tracking-wide',
                      isDark ? 'text-brand-300 bg-brand-500/12' : 'text-brand-600 bg-brand-50',
                    ].join(' ')}>
                      {unreadCount} new
                    </span>
                  )}
                  <button
                    onClick={markAllRead}
                    disabled={unreadCount === 0}
                    className={[
                      'flex h-9 w-9 -m-2 items-center justify-center gap-1 rounded-lg text-caption-sm font-medium transition-colors duration-150',
                      'disabled:opacity-40 disabled:cursor-not-allowed',
                      isDark ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600',
                    ].join(' ')}
                    title="Mark all as read"
                    aria-label="Mark all notifications as read"
                  >
                    <CheckCheck size={13} />
                  </button>
                </div>
              </div>

              {/* Notification items */}
              <div className="py-1.5 max-h-[320px] overflow-y-auto [scrollbar-width:thin]">
                {loading && notifications.length === 0 && (
                  <div className="flex items-center justify-center gap-2 py-10">
                    <Loader2 size={15} className="animate-spin text-brand-500 dark:text-brand-400" />
                    <span className="text-compact-xs text-slate-500 dark:text-slate-400">
                      Loading notifications…
                    </span>
                  </div>
                )}

                {/* Only an empty list is a true "all caught up". An error with no
                    rows is a different state and must not masquerade as one. */}
                {!loading && notifications.length === 0 && !error && (
                  <div className="flex flex-col items-center justify-center gap-1.5 py-10 px-5 text-center">
                    <div className={[
                      'w-9 h-9 rounded-xl flex items-center justify-center mb-1',
                      isDark ? 'bg-dark-icon-bg-brand-subtle' : 'bg-brand-500/[0.07]',
                    ].join(' ')}>
                      <Bell size={15} strokeWidth={2} className="text-brand-500 dark:text-brand-400" />
                    </div>
                    <p className="text-caption font-semibold text-slate-800 dark:text-white">
                      You're all caught up
                    </p>
                    <p className="text-compact-xs text-slate-500 dark:text-slate-400">
                      New updates about your care will appear here.
                    </p>
                  </div>
                )}

                {!loading && notifications.length === 0 && error && (
                  <div className="py-10 px-5 text-center">
                    <p className="text-compact-xs text-slate-500 dark:text-slate-400">
                      {error}
                    </p>
                  </div>
                )}

                {notifications.map((n) => {
                  const Icon = KIND_ICONS[n.kind] ?? Info;
                  const when = formatWhen(n.createdAt);
                  return (
                    <button
                      key={n.notifId}
                      onClick={() => handleNotificationClick(n.notifId, n.link, n.unread)}
                      className={[
                        'w-full flex items-start gap-3.5 px-5 py-3.5 text-left relative',
                        'transition-all duration-150',
                        isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-brand-50/25',
                      ].join(' ')}
                    >
                      {/* Unread accent line */}
                      {n.unread && (
                        <span className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full bg-gradient-to-b from-brand-400 to-brand-500 shadow-dark-unread-bar" />
                      )}

                      {/* Icon */}
                      <div
                        className={[
                          'flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center mt-0.5',
                          isDark ? 'bg-dark-icon-bg-brand-subtle' : 'bg-brand-500/[0.07]',
                        ].join(' ')}
                      >
                        <Icon size={15} strokeWidth={2} className={"text-brand-500 dark:text-brand-400"} />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-0.5">
                          <p className={[
                            'text-caption truncate',
                            n.unread ? 'font-semibold' : 'font-medium',
                            "text-slate-800 dark:text-white",
                          ].join(' ')}>
                            {n.title}
                          </p>
                          {when && (
                            <span className={[
                              'text-nav-badge flex-shrink-0 font-medium',
                              "text-slate-400 dark:text-slate-400",
                            ].join(' ')}>
                              {when}
                            </span>
                          )}
                        </div>
                        <p className={[
                          'text-compact-xs leading-relaxed',
                          "text-slate-500 dark:text-slate-400",
                        ].join(' ')}>
                          {n.body}
                        </p>
                      </div>

                      {n.link && (
                        <ChevronRight
                          size={13}
                          className={[
                            'flex-shrink-0 mt-3 transition-transform duration-150 group-hover:translate-x-0.5',
                            "text-slate-300 dark:text-slate-600",
                          ].join(' ')}
                        />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Footer — hidden when there is nothing further to load. */}
              {canLoadMore && (
                <div className={[
                  'px-5 py-3 border-t text-center',
                  isDark ? 'border-dark-border-subtle bg-dark-hover-subtle' : 'border-slate-100 bg-slate-50/60',
                ].join(' ')}>
                  <button
                    onClick={() => setLimit((l) => Math.min(l + PAGE_SIZE, MAX_LIMIT))}
                    disabled={loading}
                    className={[
                      'text-caption font-semibold transition-colors duration-150',
                      'disabled:opacity-50 disabled:cursor-not-allowed',
                      isDark ? 'text-brand-400 hover:text-brand-300' : 'text-brand-500 hover:text-brand-600',
                    ].join(' ')}
                  >
                    {loading ? 'Loading…' : 'Show older notifications'}
                  </button>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

export default HeaderNotifications;
